import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserMetadata } from '../../context/UserMetadataContext';
import { useAuth0Tokens } from '../../hooks/useAuth0Tokens';
import SyncButton from './SyncButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import {
  Checkbox,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Chip,
} from '@mui/material';

const DATAGRID_SX = {
  border: 0,
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: 'background.subtle',
    borderBottom: 1,
    borderColor: 'divider',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    color: 'text.secondary',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  '& .MuiDataGrid-cell': {
    color: 'text.primary',
    borderColor: 'divider',
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-footerContainer': {
    bgcolor: 'background.subtle',
    borderTop: 1,
    borderColor: 'divider',
  },
  '& .MuiDataGrid-row:hover': {
    bgcolor: 'action.hover',
  },
};

const SPIN_SX = {
  animation: 'spin 1s linear infinite',
  '@keyframes spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
};

const GitHubViewModal = ({ isOpen, onClose, orgs, setOrgs, onRefresh }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { userMetadata } = useUserMetadata();
  const { getTokens } = useAuth0Tokens();
  const [selectedRows, setSelectedRows] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [repoModalOpen, setRepoModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [pendingRepoChanges, setPendingRepoChanges] = useState({});
  const [isUpdatingRepos, setIsUpdatingRepos] = useState(false);
  const [updateMessage, setUpdateMessage] = useState(null);

  useEffect(() => {
    // Reset pending changes when modal opens with a new org
    if (selectedOrg) {
      setPendingRepoChanges({});
      setUpdateMessage(null);
    }
  }, [selectedOrg]);

  if (!isOpen) return null;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleOrgClick = (org) => {
    setSelectedOrg(org);
    setRepoModalOpen(true);
    setPendingRepoChanges({});
  };

  const handleRepoCheckboxChange = (repoName, isChecked) => {
    setPendingRepoChanges(prev => ({
      ...prev,
      [repoName]: isChecked
    }));
  };

  const handleUpdateRepoScopes = async () => {
    if (Object.keys(pendingRepoChanges).length === 0) return;

    const orgName = selectedOrg.name;
    const apiUrl = process.env.REACT_APP_API_URL;
    const apiAudience = process.env.REACT_APP_API_AUDIENCE;

    try {
      setIsUpdatingRepos(true);

      // Create updated in_scope_repos list
      let updatedInScopeRepos = [...(selectedOrg.in_scope_repos || [])];

      // Apply all pending changes
      Object.entries(pendingRepoChanges).forEach(([repoName, isChecked]) => {
        if (isChecked && !updatedInScopeRepos.includes(repoName)) {
          updatedInScopeRepos.push(repoName);
        } else if (!isChecked && updatedInScopeRepos.includes(repoName)) {
          updatedInScopeRepos = updatedInScopeRepos.filter(repo => repo !== repoName);
        }
      });

      // Update orgs state
      const updatedOrgs = orgs.map(org => {
        if (org.name === orgName) {
          return {
            ...org,
            in_scope_repos: updatedInScopeRepos
          };
        }
        return org;
      });

      // Update local state first for immediate feedback
      setOrgs(updatedOrgs);

      // Update selectedOrg state
      const updatedSelectedOrg = updatedOrgs.find(org => org.name === orgName);
      setSelectedOrg(updatedSelectedOrg);

      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: apiAudience
        }
      });

      const response = await fetch(`${apiUrl}/integrations/scope`, {
        method: 'PATCH',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
          "x-tenant-name": userMetadata?.company || '',
          "integration_type": "Github"
        },
        body: JSON.stringify({
          org_name: orgName,
          in_scope_repos: updatedInScopeRepos
        }),
      });

      if (!response.ok) {
        throw new Error(`Update scope failed with status: ${response.status}`);
      }

      // Clear pending changes after successful update
      setPendingRepoChanges({});
      setUpdateMessage({ type: 'success', text: 'Repository scopes updated successfully!' });

    } catch (error) {
      // Show error message
      setUpdateMessage({ type: 'error', text: 'Failed to update repository scopes. Please try again.' });
      await onRefresh();
    } finally {
      setIsUpdatingRepos(false);
    }
  };

  const handleSync = async (orgName) => {
    // Note: Sync is only available for IDP integrations (Entra ID, Okta)
    // GitHub integrations don't require sync as data is fetched on-demand
    alert('Sync is not available for GitHub integrations. GitHub data is fetched in real-time.');
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Organization',
      width: 250,
      flex: 2,
      renderCell: (params) => (
        <Box
          onClick={() => handleOrgClick(params.row)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            py: 1,
            '&:hover': { color: 'primary.main' },
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'linked_repos',
      headerName: 'Linked Repos',
      width: 250,
      flex: 2,
      renderCell: (params) => (
        <Typography
          variant="body2"
          onClick={() => handleOrgClick(params.row)}
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            fontWeight: 600,
            textDecoration: 'underline',
            '&:hover': { color: 'primary.dark' },
          }}
        >
          {params.row.repos ? params.row.repos.length : 0} Linked Repositories
        </Typography>
      )
    },
    {
      field: 'date',
      headerName: 'Last Synced',
      width: 250,
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 250,
      flex: 2,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'Active' ? 'success' : params.value === 'Integrating...' ? 'warning' : 'error'}
          sx={{ fontWeight: 600 }}
        />
      )
    },
    {
      field: 'comment',
      headerName: 'Comments',
      width: 250,
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      flex: 1,
      renderCell: (params) => (
        <SyncButton
          onSync={() => handleSync(params.row.name)}
          disabled={false}
        />
      )
    }
  ];

  // Transform orgs data for the main table
  const rows = orgs.map((org, index) => ({
    id: index,
    name: org.name,
    date: org.date,
    status: org.status,
    comment: org.comment,
    repos: org.repos,
    in_scope_repos: org.in_scope_repos
  }));

  const paginationModel = { page: 0, pageSize: 5 };

  const handleDelete = async () => {
    if (selectedRows.length === 0) return;

    try {
      const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;
      const { apiToken } = await getTokens();

      // Get the selected integrations to delete
      const integrationsToDelete = selectedRows.map(id => orgs[id]);

      // Delete each integration using GraphQL mutation
      const deletePromises = integrationsToDelete.map(async (integration) => {
        const mutation = `
          mutation DeleteIntegration($input: DeleteIntegrationInput!) {
            deleteIntegration(input: $input) {
              success
              message
              integrationId
              errors
            }
          }
        `;

        const variables = {
          input: {
            id: integration.id,
            integrationType: "github",
            orgName: integration.name
          }
        };

        console.log('Deleting integration:', { id: integration.id, orgName: integration.name });

        const response = await fetch(graphqlEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiToken}`
          },
          body: JSON.stringify({ query: mutation, variables })
        });

        const responseData = await response.json();
        console.log('Delete response:', responseData);

        if (responseData.errors) {
          throw new Error(responseData.errors[0]?.message || 'GraphQL error occurred');
        }

        const result = responseData.data?.deleteIntegration;
        if (!result || !result.success) {
          throw new Error(result?.message || result?.errors?.[0] || 'Failed to delete integration');
        }

        return result;
      });

      // Wait for all deletes to complete
      await Promise.all(deletePromises);

      // Update local state - remove deleted integrations
      const deletedIds = integrationsToDelete.map(i => i.id);
      const updatedOrgs = orgs.filter(org => !deletedIds.includes(org.id));
      setOrgs(updatedOrgs);
      setSelectedRows([]);

      console.log('Successfully deleted integrations');
    } catch (error) {
      console.error('Error deleting integrations:', error);
      alert(`Failed to delete integration: ${error.message}`);
    }
  };

  // Repository modal columns
  const repoColumns = [
    {
      field: 'name',
      headerName: 'Repository',
      flex: 2,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'inScope',
      headerName: 'In Scope',
      flex: 1,
      renderCell: (params) => {
        const repoName = params.row.name;
        const isInScope = pendingRepoChanges.hasOwnProperty(repoName)
          ? pendingRepoChanges[repoName]
          : params.value;

        return (
          <Box display="flex" alignItems="center">
            <Checkbox
              checked={isInScope}
              onChange={(e) => handleRepoCheckboxChange(repoName, e.target.checked)}
              sx={{ p: 0 }}
            />
          </Box>
        );
      }
    }
  ];

  // Transform repos data for the repository modal
  const getRepoRows = () => {
    if (!selectedOrg || !selectedOrg.repos) return [];

    return selectedOrg.repos.map((repo, index) => ({
      id: index,
      name: repo,
      inScope: Array.isArray(selectedOrg.in_scope_repos) &&
              selectedOrg.in_scope_repos.includes(repo)
    }));
  };

  // Check if there are any pending changes
  const hasPendingChanges = Object.keys(pendingRepoChanges).length > 0;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Linked Organizations</Typography>
          <IconButton onClick={handleRefresh} disabled={isRefreshing} size="small" color="primary">
            <RefreshIcon sx={isRefreshing ? SPIN_SX : {}} />
          </IconButton>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{ pagination: { paginationModel } }}
          pageSizeOptions={[5, 10]}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          slots={{
            toolbar: () => (
              selectedRows.length > 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, borderBottom: 1, borderColor: 'divider' }}>
                  <Button variant="contained" color="error" size="small" onClick={handleDelete}>
                    Delete selected ({selectedRows.length})
                  </Button>
                </Box>
              ) : null
            )
          }}
          sx={DATAGRID_SX}
          autoHeight
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 100}
        />

        {/* Repository Modal */}
        <Dialog
          open={repoModalOpen}
          onClose={() => setRepoModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: '8px' }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            padding: '16px 24px'
          }}>
            <Typography variant="h6" component="div">
              {selectedOrg?.name} Repositories
            </Typography>
            <IconButton onClick={() => setRepoModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ padding: '24px' }}>
            {updateMessage && (
              <Box
                sx={{
                  mb: 2,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: updateMessage.type === 'success' ? 'success.light' : 'error.light',
                  color: updateMessage.type === 'success' ? 'success.dark' : 'error.dark'
                }}
              >
                <Typography variant="body2">{updateMessage.text}</Typography>
              </Box>
            )}
            <DataGrid
              rows={getRepoRows()}
              columns={repoColumns}
              autoHeight
              disableRowSelectionOnClick
              hideFooter={getRepoRows().length <= 10}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } }
              }}
              sx={DATAGRID_SX}
              getRowHeight={() => 'auto'}
              getEstimatedRowHeight={() => 60}
            />
          </DialogContent>
          <DialogActions sx={{
            padding: '16px 24px',
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <Box>
              {hasPendingChanges && (
                <Typography variant="body2" color="error" sx={{ mr: 2 }}>
                  You have pending changes
                </Typography>
              )}
            </Box>
            <Box>
              <Button
                onClick={() => setRepoModalOpen(false)}
                sx={{ mr: 2, color: 'text.secondary' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateRepoScopes}
                variant="contained"
                color="primary"
                disabled={!hasPendingChanges || isUpdatingRepos}
                startIcon={isUpdatingRepos ? <RefreshIcon sx={SPIN_SX} /> : null}
              >
                {isUpdatingRepos ? 'Updating...' : 'Update Repositories'}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default GitHubViewModal;
